/**
 * ExportButton.js
 *
 * A simple MUI Button that triggers a browser file download from
 * GET /api/transactions/export with the provided filter parameters.
 *
 * Props:
 *   from       {string}  ISO date string (optional)
 *   to         {string}  ISO date string (optional)
 *   accountId  {string}  MongoDB ObjectId string (optional)
 *   format     {string}  'csv' | 'json'  (default 'csv')
 *   label      {string}  button label (default 'Export CSV')
 *   variant    {string}  MUI button variant (default 'outlined')
 *   size       {string}  MUI button size (default 'small')
 *   sx         {object}  extra MUI sx styles
 */

import React, { useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { API_CONFIG } from '../../config';

const API_BASE = API_CONFIG.API_BASE_URL
  ? API_CONFIG.API_BASE_URL.replace(/\/api$/, '')
  : (process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app');

export default function ExportButton({
  from,
  to,
  accountId,
  format = 'csv',
  label,
  variant = 'outlined',
  size = 'small',
  sx = {},
}) {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { format };
      if (from)      params.from      = from;
      if (to)        params.to        = to;
      if (accountId) params.accountId = accountId;

      const response = await axios.get(`${API_BASE}/api/transactions/export`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      // Build filename from date range or fallback
      const dateTag = from || to
        ? `_${(from || '').slice(0, 10)}_${(to || '').slice(0, 10)}`
        : `_${new Date().toISOString().slice(0, 10)}`;
      const ext      = format === 'json' ? 'json' : 'csv';
      const filename = `transactions${dateTag}.${ext}`;

      // Trigger browser download
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[ExportButton] Export failed:', err);
      alert('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      startIcon={loading ? <CircularProgress size={14} /> : <DownloadIcon />}
      onClick={handleExport}
      disabled={loading}
      sx={sx}
    >
      {label || (format === 'json' ? 'Export JSON' : 'Export CSV')}
    </Button>
  );
}
